import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { orgProjectsApi } from '@/lib/api'
import type { CreateProjectInput, OrgProject } from '@/lib/api'
import { queryKeys } from '@/lib/queryClient'

/**
 * Reads and writes for the org-scoped project surface.
 *
 * There are three caches that show a project list — the shell's sidebar and
 * overview read the frozen `GET /api/projects`, the board and backlog screens
 * keep their own copy of it, and this feature reads the v2 list — so a create
 * has to invalidate all three or the new project is created and then invisible
 * until a reload. That is the single most confusing failure this flow can have,
 * which is why the invalidation lives here rather than at each call site.
 */

/** Every cache key that renders a list of projects. */
function projectListKeys(slug: string): Array<readonly unknown[]> {
  return [
    queryKeys.projects, // ['projects'] — the shell's list, by prefix
    queryKeys.orgProjects(slug),
    ['v2', 'projects', 'legacy'], // features/issue/useWorkspace
  ]
}

/**
 * The workspace's projects, straight from the org-scoped endpoint.
 *
 * `includeArchived` is part of the key, not just the request: an archived
 * project still owns its key, so the create form asks for the full set while
 * the switcher asks for the working one, and neither may serve the other's
 * answer from cache.
 */
export function useOrgProjects(
  slug: string | null,
  options?: { enabled?: boolean; includeArchived?: boolean },
) {
  const includeArchived = options?.includeArchived ?? false
  return useQuery({
    queryKey: [...queryKeys.orgProjects(slug ?? ''), { includeArchived }] as const,
    queryFn: ({ signal }) =>
      orgProjectsApi.list(slug as string, { signal, includeArchived }),
    enabled: Boolean(slug) && options?.enabled !== false,
    staleTime: 60_000,
  })
}

/**
 * Creates a project.
 *
 * The mutation resolves with the stored project — key included — so the caller
 * can navigate straight to its board; the invalidation is already queued by
 * then, so the sidebar has it by the time the board renders.
 */
export function useCreateProject(slug: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateProjectInput): Promise<OrgProject> =>
      orgProjectsApi.create(slug as string, input),
    onSuccess: () => {
      if (!slug) return
      for (const queryKey of projectListKeys(slug)) {
        void queryClient.invalidateQueries({ queryKey })
      }
    },
  })
}

/** Renames, re-leads or archives a project. */
export function useUpdateProject(slug: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      ref,
      input,
    }: {
      ref: string
      input: Partial<CreateProjectInput> & { archived?: boolean }
    }): Promise<OrgProject> => orgProjectsApi.update(slug as string, ref, input),
    onSuccess: () => {
      if (!slug) return
      for (const queryKey of projectListKeys(slug)) {
        void queryClient.invalidateQueries({ queryKey })
      }
    },
  })
}
